import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, GridLegacy as Grid, Stack, TextField } from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import Spinner from '~/components/Common/Spinner';
import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import {
  buildFixedCabinMarkupRows,
  bundleCabinMarkupRowsToApi,
  mergeFixedCabinMarkupRows,
  validateBundleCabinMarkupRows,
} from '~/components/Cruises/pages/Bundles/bundle-cabin-markup-form-utils';
import BundleCabinTypeMarkupsEditor from '~/components/Cruises/pages/Bundles/components/BundleCabinTypeMarkupsEditor';
import { BundleCabinMarkupFormRow, CruiseBundle } from '~/components/Cruises/pages/Bundles/types';

import { useSaveBundle } from '~/hooks/apis/cruises/useSaveBundle';
import useAppNavigation from '~/hooks/useAppNavigation';

import { CruiseLine } from '~/services/cruises/CruiseLineService';

/** Number inputs use `''` when cleared (see handleChangeNumberField). */
type BundleFormState = Omit<Partial<CruiseBundle>, 'savingsAmount'> & {
  savingsAmount?: number | null | '';
};

const INITIAL_FORM_STATE: BundleFormState = {
  name: '',
  savingsAmount: null,
  termsAndConditions: null,
  startDate: null,
  endDate: null,
  fkVendorId: null,
};

/** Legacy bundle.markup column until the cruise contract drops it; pricing uses cabinTypeMarkups. */
const LEGACY_BUNDLE_MARKUP_PLACEHOLDER = 0;

function optionalNumberFormValue(value: number | null | '' | undefined): number | null {
  if (value === '' || value === undefined) {
    return null;
  }
  return value;
}

type Props = {
  bundle?: CruiseBundle;
};

function BundleForm({ bundle }: Props) {
  const [formValues, setFormValues] = useState<BundleFormState>(INITIAL_FORM_STATE);
  const [cabinMarkupRows, setCabinMarkupRows] = useState<Array<BundleCabinMarkupFormRow>>(buildFixedCabinMarkupRows);

  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();
  const isEditing = !!bundle;

  const saveBundleMutation = useSaveBundle();

  const isSaveEnabled = useMemo(
    () =>
      Boolean(
        formValues.name &&
          formValues.startDate &&
          formValues.endDate &&
          formValues.fkVendorId &&
          !validateBundleCabinMarkupRows(cabinMarkupRows) &&
          !saveBundleMutation.isPending,
      ),
    [formValues, cabinMarkupRows, saveBundleMutation.isPending],
  );

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: value ?? undefined }));
    }
  }, []);

  const handleChangeNumberField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, valueAsNumber } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: isNaN(valueAsNumber) ? '' : valueAsNumber }));
    }
  }, []);

  const handleChangeDateField = useCallback((value: string, name: 'startDate' | 'endDate') => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCruiseLineField = useCallback((cruiseLine: CruiseLine) => {
    setFormValues((prev) => ({ ...prev, fkVendorId: cruiseLine?.id ?? null }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (formValues.startDate && formValues.endDate && formValues.endDate <= formValues.startDate) {
      enqueueSnackbar('End date must be after start date', { variant: 'error' });
      return;
    }

    const cabinMarkupError = validateBundleCabinMarkupRows(cabinMarkupRows);
    if (cabinMarkupError) {
      enqueueSnackbar(cabinMarkupError, { variant: 'error' });
      return;
    }

    const cabinTypeMarkups = bundleCabinMarkupRowsToApi(cabinMarkupRows);

    const sharedFields = {
      name: formValues.name || '',
      markup: LEGACY_BUNDLE_MARKUP_PLACEHOLDER,
      savingsAmount: optionalNumberFormValue(formValues.savingsAmount),
      termsAndConditions: formValues.termsAndConditions || null,
      startDate: formValues.startDate || '',
      endDate: formValues.endDate || '',
      cabinTypeMarkups,
    };

    const apiData = isEditing ? sharedFields : { ...sharedFields, fkVendorId: formValues.fkVendorId || '' };

    saveBundleMutation.mutate(
      { id: bundle?.id, data: apiData },
      {
        onSuccess: (result) => {
          enqueueSnackbar('Bundle saved successfully', { variant: 'success' });
          const bundleId = bundle?.id || result?.id;
          push(`/cruises/bundles/edit/${bundleId}/items`);
        },
        onError: () => {
          enqueueSnackbar('Error saving bundle', { variant: 'error' });
        },
      },
    );
  }, [bundle?.id, cabinMarkupRows, isEditing, enqueueSnackbar, push, formValues, saveBundleMutation]);

  useEffect(() => {
    if (bundle) {
      setFormValues({
        name: bundle.name,
        savingsAmount: bundle.savingsAmount ?? null,
        termsAndConditions: bundle.termsAndConditions ?? null,
        startDate: bundle.startDate,
        endDate: bundle.endDate,
        fkVendorId: bundle.fkVendorId,
      });
      setCabinMarkupRows(mergeFixedCabinMarkupRows(bundle.cabinTypeMarkups ?? []));
    } else {
      setCabinMarkupRows(buildFixedCabinMarkupRows());
    }
  }, [bundle]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            required
            fullWidth
            name="name"
            label="Bundle Name"
            onChange={handleChangeField}
            inputProps={{ maxLength: 100 }}
            value={formValues.name}
            helperText="e.g. Sydney Pre & Post Stay Bundle"
          />
        </Grid>
        <Grid item xs={6}>
          <InputCruiseLineSelect
            label="Cruise line"
            value={formValues.fkVendorId}
            onChange={handleCruiseLineField}
            withApprovedOffers
            disabled={saveBundleMutation.isPending}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            name="savingsAmount"
            label="Savings Amount ($)"
            type="number"
            onChange={handleChangeNumberField}
            value={formValues.savingsAmount ?? ''}
            helperText="Amount customers save by booking this bundle (display only)"
            inputProps={{ min: 0, step: 1 }}
          />
        </Grid>
      </Grid>

      <Stack mt={2} spacing={2} direction="row">
        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          disablePast
          format="DD/MM/YYYY HH:mm"
          label="Bundle Start Date *"
          value={formValues.startDate}
          onChange={(value) => handleChangeDateField(value, 'startDate')}
        />

        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          disablePast
          format="DD/MM/YYYY HH:mm"
          label="Bundle End Date *"
          value={formValues.endDate}
          onChange={(value) => handleChangeDateField(value, 'endDate')}
        />
      </Stack>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="termsAndConditions"
            label="Terms & Conditions"
            multiline
            minRows={3}
            onChange={handleChangeField}
            value={formValues.termsAndConditions ?? ''}
            helperText="Bundle terms and conditions displayed to the customer (optional)"
          />
        </Grid>
      </Grid>

      <BundleCabinTypeMarkupsEditor
        rows={cabinMarkupRows}
        onChange={setCabinMarkupRows}
        disabled={saveBundleMutation.isPending}
      />

      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {saveBundleMutation.isPending && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default BundleForm;
